import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Eye, EyeOff, AlertCircle, Fingerprint } from "lucide-react";
import { Input } from "../../share/ui/input";
import loginBg from "../../assets/img/login.png";
import maskgroup from "../../assets/img/maskgroup.png";
import logo from "../../assets/logo/evocare.svg";
import { cn } from "../../lib/utils";
import { login } from "../../api/login/authService";
import { LoginResponse, LoginError } from "../../pages/login/login.types";
import { useTranslation } from "react-i18next";
import jpFrag from "../../assets/icons/japanflag.svg";
import usFrag from "../../assets/icons/usaflag.svg";
import idFrag from "../../assets/icons/indonesiaflag.svg";

import {
  REGISTER,
  REQUEST_LOGIN,
  VERIFY,
  VERIFY_LOGIN,
} from "../../api/endPoint";
import useMutation from "../../hooks/useMutattion";
import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import api from "../../api/axiosConfig";

const REMEMBER_ME_KEY = "rememberMe";
const USER_EMAIL_KEY = "rememberedUserEmail";
const PASSWORD_KEY = "rememberedPassword";
const TEMP_USER_KEY = "tempUserFor2FA";

type Errors = { email?: string; password?: string };

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState("en");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const navigate = useNavigate();
  const languages = [
    { code: "en", label: "English", flag: usFrag },
    { code: "ja", label: "日本語", flag: jpFrag },
    { code: "id", label: "Bahasa Indonesia", flag: idFrag },
  ];

  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isLangDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLangDropdownOpen]);

  // Passkey mutation hooks
  const { execute: requestLoginOptions } = useMutation(REQUEST_LOGIN, "POST");
  const { execute: verifyLoginPasskey } = useMutation(VERIFY_LOGIN, "POST");

  const { execute: registerOptions } = useMutation(REGISTER, "GET");
  const { execute: verifyRegistration } = useMutation(VERIFY, "POST");

  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBER_ME_KEY) === "true";
    const storedEmail = localStorage.getItem(USER_EMAIL_KEY);
    const storedPassword = localStorage.getItem(PASSWORD_KEY);

    if (remembered && storedEmail && storedPassword) {
      setEmail(storedEmail);
      setPassword(storedPassword);
      setRememberMe(true);
    }
    sessionStorage.removeItem(TEMP_USER_KEY);

    const storedLang = localStorage.getItem("language") || "en";
    setLang(storedLang);
    i18n.changeLanguage(storedLang);
  }, []);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
    setLang(lang);
  };

  const handleRegisterPasskey = async () => {
    setIsLoading(true);
    let excludeCredentials: any = [];
    try {
      const credentials: any = await requestLoginOptions(
        email ? { email } : {}
      );
      excludeCredentials =
        credentials && credentials.allowedCredentials
          ? credentials.allowedCredentials
          : [];

      // Step 1: Get registration options from server
      const options: any = await registerOptions();

      if (!options) {
        toast.error(t("register.errors.options"));
        return;
      }
      // Step 2: Create credentials using WebAuthn API
      // Attempt to create credentials using WebAuthn API
      // const attRes = await startRegistration({ optionsJSON: options });
      const attRes = await startRegistration({
        optionsJSON: {
          ...options,
          excludeCredentials: excludeCredentials,
        },
      });

      if (!attRes) {
        toast.error(t("register.errors.register"));
        return;
      }

      // Step 3: Send credential to server for verification and storage
      const result: any = await verifyRegistration({
        ...attRes,
        challenge: options.challenge,
      });
      if (result?.data) {
        toast.success(t("register.registerSuccess"));
      }
    } catch (error: any) {
      const errorMessage = error.message || t("register.errors.verify");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setErrors({});
    sessionStorage.removeItem(TEMP_USER_KEY);

    const newErrors: Errors = {};
    if (!email) newErrors.email = t("login.errorEmail");
    if (!password) newErrors.password = t("login.errorPassword");
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const loginResponse: LoginResponse = await login(email, password);
      const user = loginResponse.user;
      const requireOtp = loginResponse.requireOtp ?? false;

      if (requireOtp) {
        localStorage.setItem("email", JSON.stringify(loginResponse.email));
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_KEY, "true");
          localStorage.setItem(USER_EMAIL_KEY, email);
          localStorage.removeItem(PASSWORD_KEY);
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY);
          localStorage.removeItem(USER_EMAIL_KEY);
          localStorage.removeItem(PASSWORD_KEY);
        }
        toast.success("Verify your email!");
        navigate("/verification");
      } else {
        if (user.userType !== "PATIENT") {
          toast.error(t("login.noPermission"));
          return;
        }
        localStorage.setItem("user", JSON.stringify(user));
        if (user.clinicIds && user.clinicIds.length > 0) {
          localStorage.setItem("clinicIds", user.clinicIds[0]);
        }
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_KEY, "true");
          localStorage.setItem(USER_EMAIL_KEY, email);
          localStorage.setItem(PASSWORD_KEY, password);
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY);
          localStorage.removeItem(USER_EMAIL_KEY);
          localStorage.removeItem(PASSWORD_KEY);
        }
        await handleRegisterPasskey();
        toast.success(t("login.loginSuccessToast"));
        setTimeout(() => navigate("/"), 1000);
      }
    } catch (err: unknown) {
      const error = err as LoginError;
      const errorMessage =
        error?.response?.data?.message || error.message || "Login failed";
      toast.error(errorMessage);
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem(USER_EMAIL_KEY);
      localStorage.removeItem(PASSWORD_KEY);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyLogin = async (e: any) => {
    setIsPasskeyLoading(true);
    try {
      e.preventDefault();
      setErrors({});

      sessionStorage.removeItem(TEMP_USER_KEY);

      const newErrors: { email?: string; password?: string } = {};

      if (!email) newErrors.email = t("login.requiredEmail");
      setErrors(newErrors);

      if (Object.keys(newErrors).length > 0) {
        setIsPasskeyLoading(false);
        return;
      }
      // Step 1: Get authentication options from server
      const options: any = await requestLoginOptions(email ? { email } : {});

      if (!options) {
        toast.error(t("login.errors.options"));
        return;
      }
      // Step 2: Start authentication with the received options
      const authRes = await startAuthentication({ optionsJSON: options });

      if (!authRes) {
        toast.error(t("login.errors.verify"));
        return;
      }

      // Step 3: Verify authentication with server
      const loginResponse: any = await verifyLoginPasskey({
        ...authRes,
        challenge: options.challenge,
      });

      if (!loginResponse?.user) {
        toast.error(t("login.errors.authenticateWithPasskey"));
        return;
      }

      const user = loginResponse.user;

      await localStorage.setItem("accessToken", loginResponse.accessToken);
      await localStorage.setItem("refreshToken", loginResponse.refreshToken);
      api.defaults.headers.common["Authorization"] =
        `Bearer ${loginResponse.accessToken}`;

      const requireOtp = loginResponse.requireOtp ?? false;

      if (requireOtp) {
        localStorage.setItem("email", JSON.stringify(loginResponse.email));
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_KEY, "true");
          localStorage.setItem(USER_EMAIL_KEY, email);
          localStorage.removeItem(PASSWORD_KEY);
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY);
          localStorage.removeItem(USER_EMAIL_KEY);
          localStorage.removeItem(PASSWORD_KEY);
        }
        toast.success("Verify your email!");
        navigate("/verification");
      } else {
        if (user.userType !== "PATIENT") {
          toast.error(t("login.noPermission"));
          return;
        }
        localStorage.setItem("user", JSON.stringify(user));
        if (user.clinicIds && user.clinicIds.length > 0) {
          localStorage.setItem("clinicIds", user.clinicIds[0]);
        }
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_KEY, "true");
          localStorage.setItem(USER_EMAIL_KEY, email);
          localStorage.setItem(PASSWORD_KEY, password);
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY);
          localStorage.removeItem(USER_EMAIL_KEY);
          localStorage.removeItem(PASSWORD_KEY);
        }
        toast.success(t("login.loginSuccessToast"));
        setTimeout(() => navigate("/"), 1000);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || t("login.failed");
      toast.error(errorMessage);
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem(USER_EMAIL_KEY);
      localStorage.removeItem(PASSWORD_KEY);
    } finally {
      setIsLoading(false);
      setIsPasskeyLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen relative">
      {/* Left side */}
      <div className="w-full md:w-3/5 flex items-start pt-16 justify-center bg-white md:px-10 px-4 relative">
        <img
          src={maskgroup}
          alt="Mask Group"
          className="absolute top-0 left-10 z-10 pointer-events-none"
          style={{ width: 450, height: "auto" }}
        />
        <div className="max-w-md w-full relative z-20">
          {/* Language switcher và logo ngang hàng */}
          <div className="flex items-center justify-between mb-6">
            <img
              src={logo}
              alt="EvoCare Logo"
              width={230}
              height={60}
              className="mb-0"
            />
            <div ref={langDropdownRef} className="relative ml-3 w-50">
              <button
                type="button"
                className="flex items-center w-50 h-10 bg-white border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-200 shadow-md cursor-pointer hover:bg-orange-50 transition-all duration-300 ease-in-out"
                onClick={() => setIsLangDropdownOpen((prev) => !prev)}
              >
                <img
                  src={languages.find((l) => l.code === lang)?.flag || usFrag}
                  alt="Flag"
                  width={30}
                  height={30}
                  className={`inline-block mr-2 align-middle ${lang === "id" ? "rounded-full border border-gray-200" : ""}`}
                />
                {/* <span className="mr-2">{languages.find(l => l.code === lang)?.label}</span> */}
                <svg
                  className="w-4 h-4 ml-auto"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isLangDropdownOpen && (
                <div className="absolute left-0 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      className={`flex items-center w-full px-3 py-2 hover:bg-orange-50 ${lang === l.code ? "bg-orange-100" : ""}`}
                      onClick={() => {
                        changeLanguage(l.code);
                        setIsLangDropdownOpen(false);
                      }}
                    >
                      <img
                        src={l.flag}
                        alt={l.label + " flag"}
                        width={22}
                        height={22}
                        className={`inline-block mr-2 align-middle ${l.code === "id" ? "rounded-full border border-gray-200" : ""}`}
                      />
                      {/* <span>{l.label}</span> */}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            {t("login.title")}
          </h2>
          <p className="text-sm text-gray-500 mb-6">{t("login.welcome")}</p>

          <form onSubmit={handleLogin} className="space-y-8">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("login.email")}
              </label>
              <div className="relative">
                <Input
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                    if (errors.email)
                      setErrors((prev: Errors) => ({
                        ...prev,
                        email: undefined,
                      }));
                  }}
                  placeholder={t("login.enterEmail")}
                  className={cn(errors.email && "border-red-500 pr-10")}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <AlertCircle
                    className="absolute right-3 top-3 text-red-500"
                    size={18}
                  />
                )}
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("login.password")}
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPassword(e.target.value);
                    if (errors.password)
                      setErrors((prev: { password?: string }) => ({
                        ...prev,
                        password: undefined,
                      }));
                  }}
                  placeholder={t("login.enterPassword")}
                  className={cn("pr-10", errors.password && "border-red-500")}
                  aria-invalid={!!errors.password}
                />
                <div className="absolute inset-y-0 right-3 flex items-center text-gray-500 cursor-pointer">
                  {showPassword ? (
                    <EyeOff size={18} onClick={() => setShowPassword(false)} />
                  ) : (
                    <Eye size={18} onClick={() => setShowPassword(true)} />
                  )}
                </div>
                {errors.password && (
                  <AlertCircle
                    className="absolute right-8 top-3 text-red-500"
                    size={18}
                  />
                )}
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center ">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                {t("login.rememberMe")}
              </label>
              <a
                href="/forgot-password"
                className="text-orange-600 hover:underline text-sm"
              >
                {t("login.forgotPassword")}
              </a>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {t("login.loading")}
                </span>
              ) : (
                t("login.button")
              )}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t("login.or")}
                </span>
              </div>
            </div>

            {/* Passkey Login Button */}
            <button
              type="button"
              onClick={(e) => handlePasskeyLogin(e)}
              className="w-full py-3 bg-blue-600 shadow-lg hover:bg-blue-700 text-white rounded-md font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={isPasskeyLoading || isLoading}
            >
              {isPasskeyLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t("login.loading")}
                </span>
              ) : (
                <>
                  <Fingerprint size={20} />
                  {t("login.loginWithPasskey")}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
      {/* Right side */}
      <div className="hidden md:block md:w-4/5 relative">
        <img
          src={loginBg}
          alt="Login Background"
          className="object-cover brightness-95 w-full h-full"
        />
      </div>
    </div>
  );
};

export default LoginPage;
